import { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import api, { apiErrorMessage } from '../api/client'
import { useProjectShares } from '../api/hooks'
import { projectRoleLabels, type ProjectRole } from '../api/types'

const shareRoleOptions = (['reader', 'collaborator'] as const).map(
  (role) => [role, projectRoleLabels[role]] as const,
)

interface ShareDialogProps {
  projectId: number
  open: boolean
  onClose: () => void
}

export default function ShareDialog({ projectId, open, onClose }: ShareDialogProps) {
  const { data: shares, loading, error, reload } = useProjectShares(projectId)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Exclude<ProjectRole, 'owner'>>('reader')
  const [actionError, setActionError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const addShare = async () => {
    setSubmitting(true)
    setActionError('')
    try {
      await api.post(`/projects/${projectId}/shares`, { email: email.trim(), role })
      setEmail('')
      reload()
    } catch (err) {
      setActionError(apiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const changeRole = async (userId: number, newRole: Exclude<ProjectRole, 'owner'>) => {
    setActionError('')
    try {
      await api.patch(`/projects/${projectId}/shares/${userId}`, { role: newRole })
      reload()
    } catch (err) {
      setActionError(apiErrorMessage(err))
    }
  }

  const removeShare = async (userId: number) => {
    setActionError('')
    try {
      await api.delete(`/projects/${projectId}/shares/${userId}`)
      reload()
    } catch (err) {
      setActionError(apiErrorMessage(err))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Partager le projet</DialogTitle>
      <DialogContent>
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 2 }}>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Rôle"
            value={role}
            onChange={(e) => setRole(e.target.value as Exclude<ProjectRole, 'owner'>)}
            size="small"
            sx={{ minWidth: 160 }}
          >
            {shareRoleOptions.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={addShare} disabled={submitting || !email.trim()}>
            Ajouter
          </Button>
        </Stack>

        {!loading && shares && shares.length === 0 && (
          <Alert severity="info">Ce projet n'est partagé avec personne.</Alert>
        )}

        <List dense disablePadding>
          {shares?.map((share) => (
            <ListItem
              key={share.id}
              disableGutters
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 140px auto',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
              }}
            >
              <Typography variant="body2" noWrap title={share.email} sx={{ minWidth: 0 }}>
                {share.email}
              </Typography>
              <TextField
                select
                value={share.role}
                onChange={(e) =>
                  changeRole(share.userId, e.target.value as Exclude<ProjectRole, 'owner'>)
                }
                size="small"
                fullWidth
              >
                {shareRoleOptions.map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton edge="end" onClick={() => removeShare(share.userId)}>
                <DeleteIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}
